// ============================================================================
// Finance view PAGE 2 — the Portfolio (CW-Q). Bryan signed this off 2026-08-12.
//
// One row per account, expandable to buildings with real names and facility IDs.
// Group by service line, territory or account manager; filter to below-target or
// needs-a-database-fix; sort by dollars or margin.
//
// TWO INVARIANTS, both load-bearing:
//   1. It opens on the SAME as-recorded basis as Page 1, aggregating the same
//      view, so the two tie the moment either is opened.
//   2. The corrections toggle exists ONLY while v_finance_corrections returns
//      rows. When the last fix lands the toggle disappears without a code
//      change — which is what "temporary by design" has to mean to be true.
// ============================================================================

import { Fragment, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  ACCOUNT_LEVEL_MONEY_CLASS,
  BAND_CLASSES,
  BUILDING_LEVEL_MONEY_CLASS,
  LEVEL_MIX_NOTE,
  SERVICE_LINE_ORDER,
  TERRITORY_ORDER,
  TERRITORY_STYLES,
  bandLabel,
  dollarsPerYearToTarget,
  fmtMoney,
  fmtPct,
  fmtPp,
  groupBy,
  isTerritory,
  marginBand,
  monthLabel,
  namedManagers,
  rollUp,
  sumToTarget,
} from './finance-lib';
import { RemovedTile } from './finance-chrome';
import { REPORTED_MONTH, type FinanceData, type FinanceRow } from './use-finance-data';

type GroupKey = 'none' | 'service_line' | 'territory' | 'manager';
type FilterKey = 'all' | 'below_target' | 'needs_fix';
type SortKey = 'dollars' | 'margin';

export function Portfolio({
  data,
  initialFilter,
  expandFirst = false,
}: {
  data: FinanceData;
  initialFilter?: FilterKey;
  /**
   * Arriving from a Page 1 click ("See which ones", "Work it") means the user
   * has already asked the question the top row answers, so the top row opens.
   * Landing on the tab directly does not expand anything.
   */
  expandFirst?: boolean;
}) {
  const [group, setGroup] = useState<GroupKey>('none');
  const [filter, setFilter] = useState<FilterKey>(initialFilter ?? 'all');
  const [sort, setSort] = useState<SortKey>('dollars');
  const [corrected, setCorrected] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [autoExpanded, setAutoExpanded] = useState(false);

  const t = data.thresholds;
  const monthName = monthLabel(REPORTED_MONTH);

  // The corrected basis is applied HERE, to the row set, so switching it cannot
  // change anything else about how the page aggregates.
  const rows = useMemo<FinanceRow[]>(() => {
    if (!corrected || data.corrections.length === 0) return data.rows;
    const byFacility = new Map(data.corrections.map((c) => [c.facility_id, c]));
    return data.rows.map((r) => {
      const c = byFacility.get(r.facility_id);
      if (!c || c.field !== 'sub_cost') return r;
      return { ...r, sub_cost: c.corrected };
    });
  }, [corrected, data.rows, data.corrections]);

  const header = useMemo(() => rollUp(rows), [rows]);

  const accounts = useMemo(() => {
    const grouped = groupBy(rows, (r) => r.account_id ?? `~${r.facility_id}`);
    return Array.from(grouped.entries()).map(([id, set]) => ({
      id,
      name: set[0]?.account_name ?? set[0]?.facility_name ?? id,
      managers: namedManagers(set.map((s) => s.account_manager_name)),
      serviceLines: Array.from(new Set(set.map((s) => s.service_line))),
      territories: Array.from(new Set(set.map((s) => s.territory ?? 'Unmapped'))),
      buildings: set,
      r: rollUp(set),
    }));
  }, [rows]);

  const needsFixIds = useMemo(
    () => new Set(data.dataQuality.map((d) => d.facility_id)),
    [data.dataQuality],
  );

  const visible = useMemo(() => {
    if (!t) return accounts;
    let out = accounts;
    if (filter === 'below_target') {
      out = out.filter((a) => a.r.marginPct !== null && a.r.marginPct < t.target_pct);
    } else if (filter === 'needs_fix') {
      out = out.filter((a) => a.buildings.some((b) => needsFixIds.has(b.facility_id)));
    }
    return [...out].sort((a, b) =>
      sort === 'dollars'
        ? b.r.revenue - a.r.revenue
        : (a.r.marginPct ?? 999) - (b.r.marginPct ?? 999),
    );
  }, [accounts, filter, sort, t, needsFixIds]);

  const fixList = useMemo(
    () =>
      t
        ? sumToTarget(
            visible.map((a) => dollarsPerYearToTarget(a.r.revenue, a.r.marginPct, t)),
          )
        : 0,
    [visible, t],
  );

  if (!t) {
    return (
      <div className="mx-auto max-w-7xl p-4 md:p-6">
        <RemovedTile
          title="Portfolio"
          prerequisite="The margin bands are missing from app_settings. Nothing here can be banded against a threshold that was invented in the client."
        />
      </div>
    );
  }

  // Seed the top row open exactly once, and only when we were sent here by a
  // question. Doing it in render (not an effect) keeps the very first paint
  // correct — an effect would flash a collapsed row first.
  if (expandFirst && !autoExpanded && visible.length > 0) {
    setAutoExpanded(true);
    setOpen(new Set([`${group === 'none' ? 'all' : visible[0].serviceLines[0]}:${visible[0].id}`]));
  }

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const groups: { key: string; label: string; accounts: typeof visible }[] =
    group === 'none'
      ? [{ key: 'all', label: '', accounts: visible }]
      : group === 'service_line'
        ? SERVICE_LINE_ORDER.map((sl) => ({
            key: sl,
            label: sl,
            accounts: visible.filter((a) => a.serviceLines.includes(sl)),
          })).filter((g) => g.accounts.length > 0)
        : group === 'territory'
          ? TERRITORY_ORDER.map((terr) => ({
              key: terr,
              label: terr,
              accounts: visible.filter((a) => a.territories.includes(terr)),
            })).filter((g) => g.accounts.length > 0)
          : Array.from(new Set(visible.map((a) => a.managers)))
              .sort()
              .map((m) => ({
                key: m,
                label: m,
                accounts: visible.filter((a) => a.managers === m),
              }));

  return (
    <div className="mx-auto max-w-7xl p-4 pb-10 md:p-6">
      {/* Header — must tie to Page 1 §1 to the cent. */}
      <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
        <div className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
          Portfolio — {monthName} · {corrected ? 'corrected basis' : 'as recorded'}
        </div>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
          <span className="tabular-nums text-tcs-text">
            <strong>{header.buildings}</strong> buildings
          </span>
          <span className="tabular-nums text-tcs-text">
            <strong>{fmtMoney(header.revenue, { cents: true })}</strong> revenue / mo
          </span>
          <span className="tabular-nums text-tcs-text">
            <strong>{fmtMoney(header.subCost, { cents: true })}</strong> sub cost / mo
          </span>
          <span
            className={`tabular-nums ${BAND_CLASSES[marginBand(header.marginPct, t)].text}`}
          >
            <strong>{fmtPct(header.marginPct, 2)}</strong> labour margin
          </span>
          <span className="tabular-nums text-tcs-text-secondary">
            {new Set(rows.map((r) => r.account_id)).size} accounts
          </span>
        </div>
        <p className="mt-1 text-[11px] text-tcs-text-muted">
          {corrected
            ? 'Corrected basis — this does NOT tie to Page 1 by design. Switch back to as-recorded to reconcile.'
            : 'As-recorded basis, aggregated from the same view as Page 1 §1. These four figures must match the Executive Overview to the cent.'}
        </p>
      </div>

      {/* Controls */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <Control
          label="Group by"
          value={group}
          onChange={(v) => setGroup(v as GroupKey)}
          options={[
            ['none', 'Nothing'],
            ['service_line', 'Service line'],
            ['territory', 'Territory'],
            ['manager', 'Account manager'],
          ]}
        />
        <Control
          label="Filter"
          value={filter}
          onChange={(v) => setFilter(v as FilterKey)}
          options={[
            ['all', 'Everything'],
            ['below_target', `Below target (${fmtPct(t.target_pct)})`],
            ['needs_fix', 'Needs a database fix'],
          ]}
        />
        <Control
          label="Sort"
          value={sort}
          onChange={(v) => setSort(v as SortKey)}
          options={[
            ['dollars', 'Dollars'],
            ['margin', 'Margin, worst first'],
          ]}
        />

        {/* D6: the toggle exists only while there are corrections to apply. */}
        {data.corrections.length > 0 && (
          <label className="flex items-center gap-2 rounded border border-st-warn-border bg-st-warn-bg px-2 py-1 text-st-warn-fg">
            <input
              type="checkbox"
              checked={corrected}
              onChange={(e) => setCorrected(e.target.checked)}
              className="h-3 w-3"
            />
            <span>
              Apply the {data.corrections.length} known correction
              {data.corrections.length === 1 ? '' : 's'} (temporary)
            </span>
          </label>
        )}
      </div>

      {data.corrections.length > 0 && (
        <details className="mt-2 rounded border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-[11px] text-tcs-text-secondary">
          <summary className="cursor-pointer">
            What the corrections are, and who owns each
          </summary>
          <ul className="mt-1 space-y-1">
            {data.corrections.map((c) => (
              <li key={`${c.facility_id}-${c.field}`}>
                <span className="text-tcs-text">{c.facility_id}</span> · {c.field}:{' '}
                <span className="tabular-nums">{fmtMoney(c.as_recorded)}</span> as recorded
                → <span className="tabular-nums">{fmtMoney(c.corrected)}</span> corrected.{' '}
                {c.correction_reason} Worth{' '}
                <span className="tabular-nums">{fmtMoney(c.annual_impact)}</span>/yr. Owner:{' '}
                {c.owner}, raised {c.raised_on}.
              </li>
            ))}
          </ul>
          <p className="mt-1">
            This layer is temporary by design. Each row is deleted from{' '}
            <code>v_finance_corrections</code> as its fix lands, and when the register is
            empty this toggle disappears on its own.
          </p>
        </details>
      )}

      <p className="mt-2 text-[11px] text-tcs-text-muted">{LEVEL_MIX_NOTE}</p>

      {/* Groups */}
      {groups.map((g) => (
        <div key={g.key} className="mt-4">
          {g.label && (
            <h3 className="flex items-center gap-2 text-sm font-medium text-tcs-text">
              {isTerritory(g.label) && (
                <span
                  className={`inline-block h-2.5 w-2.5 rounded-sm ${TERRITORY_STYLES[g.label].swatchClass}`}
                  aria-hidden
                />
              )}
              {g.label}
              <span className="font-normal text-tcs-text-muted">
                {g.accounts.length} account{g.accounts.length === 1 ? '' : 's'}
              </span>
            </h3>
          )}
          <div className="mt-1 overflow-hidden rounded-lg border border-tcs-border">
            <table className="w-full text-xs">
              <thead className="bg-tcs-bg-muted">
                <tr className="text-left text-tcs-text-muted">
                  <th className="w-6 px-2 py-1.5" />
                  <th className="px-2 py-1.5 font-medium">Account</th>
                  <th className="px-2 py-1.5 font-medium">Manager</th>
                  <th className="px-2 py-1.5 text-right font-medium">Buildings</th>
                  <th className="px-2 py-1.5 text-right font-medium">Revenue / mo</th>
                  <th className="px-2 py-1.5 text-right font-medium">Sub cost / mo</th>
                  <th className="px-2 py-1.5 text-right font-medium">Margin</th>
                  <th className="px-2 py-1.5 text-right font-medium">vs target</th>
                  <th className="px-2 py-1.5 text-right font-medium">$/yr to target</th>
                </tr>
              </thead>
              <tbody>
                {g.accounts.map((a) => {
                  const band = marginBand(a.r.marginPct, t);
                  const fix = dollarsPerYearToTarget(a.r.revenue, a.r.marginPct, t);
                  const rowKey = `${g.key}:${a.id}`;
                  const isOpen = open.has(rowKey);
                  return (
                    // Keyed Fragment, not a bare <>: the account row and its
                    // building rows are siblings in one <tbody>, so an unkeyed
                    // fragment makes React re-key every expanded row on each
                    // filter change and warns in the console.
                    <Fragment key={rowKey}>
                      <tr
                        className="cursor-pointer border-t border-tcs-border/60 hover:bg-tcs-surface"
                        onClick={() => toggle(rowKey)}
                      >
                        <td className="px-2 py-1.5 text-tcs-text-muted">
                          {isOpen ? (
                            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                          ) : (
                            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-tcs-text">{a.name}</td>
                        <td className="px-2 py-1.5 text-tcs-text-secondary">{a.managers}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-tcs-text-secondary">
                          {a.r.buildings}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-tcs-text">
                          {fmtMoney(a.r.revenue)}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-tcs-text-secondary">
                          {fmtMoney(a.r.subCost)}
                        </td>
                        <td
                          className={`px-2 py-1.5 text-right tabular-nums ${BAND_CLASSES[band].text}`}
                          title={bandLabel(band, t)}
                        >
                          {fmtPct(a.r.marginPct)}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-tcs-text-secondary">
                          {fmtPp(a.r.marginPct === null ? null : a.r.marginPct - t.target_pct)}
                        </td>
                        <td
                          className={`px-2 py-1.5 text-right tabular-nums ${ACCOUNT_LEVEL_MONEY_CLASS}`}
                          title={fix?.workings ?? 'At or above target — nothing to fix, and never netted against accounts below target.'}
                        >
                          {fix ? fmtMoney(fix.amount) : '—'}
                        </td>
                      </tr>
                      {isOpen &&
                        a.buildings
                          .slice()
                          .sort((x, y) => y.revenue - x.revenue)
                          .map((b) => {
                            const bMargin = rollUp([b]);
                            const bBand = marginBand(bMargin.marginPct, t);
                            const bFix = dollarsPerYearToTarget(b.revenue, bMargin.marginPct, t);
                            return (
                              <tr
                                key={`${rowKey}:${b.facility_id}`}
                                className="border-t border-tcs-border/40 bg-tcs-bg-muted/50"
                              >
                                <td className="px-2 py-1" />
                                <td className="px-2 py-1 pl-6 text-tcs-text-secondary">
                                  <span className="font-mono text-[10px] text-tcs-text-muted">
                                    {b.facility_id}
                                  </span>{' '}
                                  {b.facility_name}
                                  {b.is_hospital && b.basis_label && (
                                    <span className="ml-1 rounded bg-st-warn-bg px-1 text-[9px] text-st-warn-fg">
                                      {b.basis_label}
                                    </span>
                                  )}
                                  {needsFixIds.has(b.facility_id) && (
                                    <span className="ml-1 rounded bg-st-danger-bg px-1 text-[9px] text-st-danger-fg">
                                      needs a database fix
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-1 text-tcs-text-muted">
                                  {b.account_manager_name ?? 'Unassigned'}
                                </td>
                                <td className="px-2 py-1 text-right text-tcs-text-muted">
                                  {b.service_line}
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums text-tcs-text-secondary">
                                  {b.revenue_basis === 'tracked' ? (
                                    fmtMoney(b.revenue)
                                  ) : (
                                    <span className="text-tcs-text-muted">—</span>
                                  )}
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums text-tcs-text-muted">
                                  {b.has_recorded_cost ? fmtMoney(b.sub_cost) : 'not recorded'}
                                </td>
                                <td
                                  className={`px-2 py-1 text-right tabular-nums ${BAND_CLASSES[bBand].text}`}
                                >
                                  {b.has_recorded_cost ? fmtPct(bMargin.marginPct) : '—'}
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums text-tcs-text-muted">
                                  {b.has_recorded_cost
                                    ? fmtPp(
                                        bMargin.marginPct === null
                                          ? null
                                          : bMargin.marginPct - t.target_pct,
                                      )
                                    : '—'}
                                </td>
                                <td
                                  className={`px-2 py-1 text-right tabular-nums ${BUILDING_LEVEL_MONEY_CLASS}`}
                                  title={
                                    bFix?.workings ??
                                    'At or above target, or no recorded cost. Building figures do not sum to the account figure.'
                                  }
                                >
                                  {bFix ? fmtMoney(bFix.amount) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="mt-4 rounded-lg border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-secondary">
        <strong className="text-tcs-text">
          {visible.length} account{visible.length === 1 ? '' : 's'} shown
        </strong>{' '}
        ·{' '}
        <span
          className="cursor-help tabular-nums underline decoration-dotted"
          title="Sum over ONLY the shown accounts that are below target. Never netted against accounts at or above target."
        >
          {fmtMoney(fixList)}/yr to reach target
        </span>{' '}
        — the size of the fix list, not a loss.
      </div>

      {visible.length === 0 && (
        <p className="mt-3 text-xs text-tcs-text-secondary">
          Nothing matches this filter. That is a result, not an empty state.
        </p>
      )}
    </div>
  );
}

function Control({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-tcs-text-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-tcs-border-input bg-tcs-field px-1.5 py-1 text-tcs-text"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}
